import { Link, Outlet, useNavigate } from 'react-router-dom';
import { AppHeader } from '@/components/shared/AppHeader';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { ArrowLeft, Pin, PinOff } from 'lucide-react';
import { SimplesNacionalProvider } from './SimplesNacionalProvider';
import { useSimplesNacionalLayout } from './useSimplesNacionalLayout';
import { useSimplesNacional } from '@/contexts/SimplesNacionalContext';

function AppHeaderSN() {
  const { buscarHeader } = useSimplesNacional();
  return <AppHeader compact hideLogo snac="S" showPeriodo onBuscar={buscarHeader ?? undefined} />;
}

export function SimplesNacionalLayout() {
  const navigate = useNavigate();
  const {
    pathname,
    navItems,
    isActive,
    pinned,
    setPinned,
    expanded,
    handleMouseEnter,
    handleMouseLeave,
  } = useSimplesNacionalLayout();

  return (
    <SimplesNacionalProvider>
      <TooltipProvider delayDuration={0}>
        <div className="flex h-screen bg-background overflow-hidden relative">
          {/* Espaçador no flow */}
          <div
            className={cn(
              'shrink-0 transition-[width] duration-200 ease-linear',
              pinned ? 'w-[270px]' : 'w-[58px]'
            )}
          />

          {/* Sidebar */}
          <nav
            className={cn(
              'absolute left-0 top-0 bottom-0 z-40 flex flex-col border-r bg-card transition-[width] duration-200 ease-linear overflow-hidden',
              expanded ? 'w-[270px]' : 'w-[58px]',
              expanded && !pinned ? 'shadow-xl' : 'shadow-[1px_0_4px_0_rgba(0,0,0,0.06)]'
            )}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {/* Logo + Título + Pin */}
            <div
              className={cn(
                'flex items-center gap-2 overflow-hidden shrink-0 h-[72px]',
                expanded ? 'px-3' : 'px-0 justify-center'
              )}
            >
              <Link to="/" className="shrink-0">
                <div className="h-9 w-9 relative overflow-hidden cursor-pointer hover:opacity-80 transition-opacity rounded-md">
                  <img
                    src="/logo-it-works.png"
                    alt="IT Works"
                    width={36}
                    height={36}
                    className="absolute top-0 left-0 object-cover object-left h-9"
                  />
                </div>
              </Link>
              {expanded && (
                <>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">Fiscal HUB</p>
                    <p className="text-xs font-semibold text-muted-foreground truncate">
                      WORKSPACE
                    </p>
                  </div>
                  <button
                    onClick={() => setPinned(v => !v)}
                    className="shrink-0 inline-flex items-center justify-center rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                    aria-label={pinned ? 'Desafixar menu' : 'Fixar menu'}
                    title={pinned ? 'Desafixar menu' : 'Fixar menu'}
                  >
                    {pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                  </button>
                </>
              )}
            </div>

            {/* Itens de navegação */}
            <div className="flex flex-col flex-1 gap-1 p-1 pt-2 overflow-y-auto overflow-x-hidden">
              {navItems.map(({ icon: Icon, label, to, exact }) => {
                const active = isActive(pathname, to, exact);
                const btn = (
                  <Link
                    to={to}
                    className={cn(
                      'flex items-center gap-3 rounded-md py-2 text-sm transition-colors w-full',
                      expanded ? 'px-2' : 'justify-center px-0',
                      active
                        ? 'bg-accent text-accent-foreground font-medium'
                        : 'text-foreground hover:bg-accent hover:text-accent-foreground'
                    )}
                  >
                    <Icon className="h-5 w-5 shrink-0" />
                    {expanded && <span className="truncate">{label}</span>}
                  </Link>
                );

                if (expanded) return <div key={to}>{btn}</div>;

                return (
                  <Tooltip key={to}>
                    <TooltipTrigger asChild>{btn}</TooltipTrigger>
                    <TooltipContent side="right">{label}</TooltipContent>
                  </Tooltip>
                );
              })}
            </div>

            {/* Footer: botão voltar */}
            <div className="p-1 pb-2 border-t shrink-0">
              {expanded ? (
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center gap-3 rounded-md px-2 py-2 text-sm text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors w-full"
                >
                  <ArrowLeft className="h-5 w-5 shrink-0" />
                  <span className="truncate">Voltar</span>
                </button>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => navigate('/')}
                      className="flex items-center justify-center rounded-md px-2 py-2 text-sm text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors w-full"
                    >
                      <ArrowLeft className="h-5 w-5 shrink-0" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="right">Voltar ao Início</TooltipContent>
                </Tooltip>
              )}
            </div>
          </nav>

          {/* Área principal */}
          <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
            <AppHeaderSN />
            <main className="flex-1 overflow-auto">
              <Outlet />
            </main>
          </div>
        </div>
      </TooltipProvider>
    </SimplesNacionalProvider>
  );
}
